import type { TimelineItem } from '../types'

export const timelineData: TimelineItem[] = [
  {
    year: 2025,
    type: 'present',
    category: 'career',
    title: 'Founding Engineer & Technical Lead, GridVista Platform',
    company: 'CTC Global',
    location: 'Irvine, CA',
    description:
      'Founding engineer, owning a scope normally split across five engineering roles; architecture, cloud infrastructure, security and compliance, data pipeline, CI/CD, and full-stack delivery; for a NERC CIP-aligned, multi-tenant SaaS platform streaming fiber-optic (BOTDR/DAS) grid telemetry from BigQuery to utility operators in near real time.',
    achievements: [
      'Took the platform from zero to a production-bound MVP; designed and delivered a working prototype demonstrated to Google within the first month, securing a multi-million-dollar partnership',
      'Architected the NERC CIP security model and authored the audit-evidence package (5 compliance documents, 2 ADRs) mapping to CIP-004/005/007/011/013',
      'Implemented defense-in-depth authorization: deny-by-default database rules, server-side tenant resolution, and a 4-tier RBAC hierarchy backed by JWT verification middleware',
      'Designed an append-only, tamper-evident audit subsystem across 5 category-partitioned collections with 26 tenant-scoped composite indexes',
      'Eliminated long-lived cloud credentials via Workload Identity Federation; hardened the perimeter with strict CORS, HTTPS-only transport, and an authenticated proxy',
      'Cut warehouse spend 10–100× by aligning the LookML semantic layer with partition pruning and clustering; projecting $100K–$500K in annual savings',
      'Grew CI-gated test coverage to 600+ tests across 130+ files at a 100% pass rate',
    ],
    technologies: [
      'React 19',
      'TypeScript',
      'Vite',
      'TanStack Query',
      'Node.js',
      'Cloud Functions v2',
      'Firebase Auth/Firestore',
      'BigQuery',
      'LookML',
      'GCP IAM',
      'GitHub Actions (OIDC)',
    ],
  },
  {
    year: 2025,
    type: 'milestone',
    category: 'education',
    title: 'M.S. Information Technology',
    company: 'Illinois Institute of Technology',
    location: 'Chicago, IL',
    description:
      "Earned Master's degree in Information Technology specializing in Cloud Computing, Operating System Virtualization, and Software as a Service. Focused on algorithms, data structures, open source programming, and cloud security.",
    achievements: [
      'Specialized in Cloud Computing (IaaS, PaaS, SaaS)',
      'Mastered Algorithms, Data Structures, and Time Complexity',
      'Advanced coursework in Operating System Virtualization',
      'Focused on Cloud Security and Open Source Programming',
    ],
    technologies: [
      'Computer Science',
      'Algorithms',
      'Cloud Computing',
      'AWS',
      'Virtualization',
      'Security',
    ],
  },
  {
    year: 2023,
    type: 'milestone',
    category: 'education',
    title: 'Post-Graduate Degree - Full-Stack Development',
    company: 'MIT',
    location: 'Cambridge, MA',
    description:
      'Completed advanced full-stack development program at MIT, specializing in modern web technologies, cloud architecture, and scalable system design.',
    achievements: [
      'Specialized in React, Node.js, and cloud DevOps',
      'Built enterprise-scale applications',
      'Mastered GraphQL and microservices architecture',
      'Completed advanced AWS and Docker certifications',
    ],
    technologies: ['React', 'Node.js', 'GraphQL', 'AWS', 'Docker', 'Microservices'],
  },
  {
    year: 2022,
    type: 'milestone',
    category: 'education',
    title: 'Started Graduate Program',
    company: 'Illinois Institute of Technology',
    location: 'Chicago, IL',
    description:
      "Began Master's program in Information Technology at Illinois Institute of Technology. Focused on cloud computing technologies, operating system virtualization, open source programming, and cloud security.",
    achievements: [
      'Accepted into IIT graduate program',
      'Specialized in Cloud Computing and Virtualization',
      'Studied Hardware, OS Foundations, and Data Networks',
      'Focused on SaaS, PaaS, IaaS architectures',
    ],
    technologies: [
      'Computer Science',
      'Cloud Computing',
      'Algorithms',
      'AWS',
      'Virtualization',
      'Security',
    ],
  },
  {
    year: 2020,
    type: 'milestone',
    category: 'career',
    title: 'Senior Software Engineer',
    company: 'Nike',
    location: 'Beaverton, OR',
    description:
      "Led front-end and BFF development for Nike's global price-optimization platform; the system merchandisers use to set and approve pricing across international markets; delivering $15M in savings per quarter, from Feb 2020 to Sep 2025.",
    achievements: [
      'Delivered $15M in savings per quarter on the global price-optimization platform',
      'Shipped a React Native companion app moving price approvals to real-time mobile sign-off, cutting the decision cycle from 2 days to 15 minutes',
      'Re-architected the React/Next.js merchandising portal with route-level code splitting, SSR, and bundle budgets; reducing First Contentful Paint by 40%',
      'Published an internal component library and shared GraphQL client layer adopted across multiple product teams',
      'Integrated SSO and RBAC into internal tooling so pricing authority matched org hierarchy',
      'Mentored 4 junior engineers and drove team-wide adoption of TypeScript and testing standards',
    ],
    technologies: [
      'React',
      'React Native',
      'Next.js',
      'TypeScript',
      'GraphQL',
      'Node.js',
      'AWS',
      'Docker',
      'Jenkins',
      'Terraform',
    ],
  },
  {
    year: 2018,
    type: 'milestone',
    category: 'career',
    title: 'Senior React / UI Engineer',
    company: 'Phillips Auction House',
    location: 'New York, NY',
    description:
      'Re-architected the flagship live-auction platform into an isomorphic React + React Native codebase on a unified design system, improving mobile load speed 50%. Built the React Native ⇔ Swift bridge powering high-fidelity AR previews that let collectors place artwork in their own space.',
    achievements: [
      'Increased mobile load speed by 50% with an isomorphic React + React Native architecture',
      'Built the AR preview feature that drove $44M in incremental 2019 sales and lifted rendering from 45 to 60 fps on iPhone 8+',
      'Delivered GraphQL and REST bidding features engineered for 200K concurrent bidders, hardening the live-bid path against race conditions and duplicate submission',
      'Raised automated UI and API coverage to 85% with Cypress and Postman',
    ],
    technologies: [
      'React',
      'React Native',
      'TypeScript',
      'Swift',
      'GraphQL',
      'Node.js',
      'AWS',
      'Cypress',
    ],
  },
  {
    year: 2015,
    type: 'milestone',
    category: 'career',
    title: 'Front-End Engineer',
    company: 'New York Life',
    location: 'New York, NY',
    description:
      'Migrated a legacy JSP/Dojo policyholder portal to a modern React SPA inside a regulated financial-services environment with strict data-handling and accessibility requirements. Implemented server-side rendering with Node.js, improving first meaningful paint from 4.2s to 2.6s.',
    achievements: [
      'Migrated legacy portal to a modern React SPA under strict data-handling and accessibility requirements',
      'Improved first meaningful paint from 4.2s to 2.6s with SSR',
      'Established automated test coverage using Mocha and Gulp',
      'Maintained a shared component library that cut UI redundancy and standardized accessible form patterns',
    ],
    technologies: ['React', 'JavaScript', 'Node.js', 'Bootstrap', 'Mocha', 'Gulp'],
  },
]
